import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Printer, Trash2, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { rupiah, MONTHS, formatDate } from "@/lib/format";
import { waLink, waMessages } from "@/lib/whatsapp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const now = new Date();
const YEARS = Array.from({ length: 5 }, (_, i) => now.getFullYear() - 2 + i);
const PAYMENT_TYPES = ["Pendaftaran Murid Baru", "SPP", "Buku", "Ujian", "Lomba", "Lainnya"];
const TYPE_STYLES = {
  "Pendaftaran Murid Baru": "bg-sky-100 text-sky-700 hover:bg-sky-100",
  "SPP": "bg-primary/10 text-primary hover:bg-primary/10",
  "Buku": "bg-amber-100 text-amber-700 hover:bg-amber-100",
  "Ujian": "bg-rose-100 text-rose-600 hover:bg-rose-100",
  "Lomba": "bg-emerald-100 text-emerald-700 hover:bg-emerald-100",
  "Lainnya": "bg-slate-200 text-slate-600 hover:bg-slate-200",
};
const EMPTY_FORM = { student_id: "", payment_type: "SPP", month: now.getMonth() + 1, year: now.getFullYear(), amount: "", method: "Tunai", account: "Kas", description: "", notes: "" };
const ACCOUNT_OPTIONS = ["Kas", "Bank", "Kas Kecil"];

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [month, setMonth] = useState("all");
  const [year, setYear] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = () => {
    const params = {};
    if (month !== "all") params.month = Number(month);
    if (year !== "all") params.year = Number(year);
    if (typeFilter !== "all") params.payment_type = typeFilter;
    api.get("/payments", { params }).then((res) => setPayments(res.data)).catch(() => {});
  };

  useEffect(() => { load(); }, [month, year, typeFilter]);
  useEffect(() => {
    api.get("/students", { params: { status: "Aktif" } }).then((res) => setStudents(res.data)).catch(() => {});
    api.get("/classes").then((res) => setClasses(res.data)).catch(() => {});
  }, []);

  const pickStudent = (id) => {
    const s = students.find((x) => x.id === id);
    const cls = classes.find((c) => c.id === s?.class_id);
    const gradeFee = s?.grade ? cls?.grade_fees?.[s.grade] : null;
    setForm((f) => ({ ...f, student_id: id,
      amount: s && f.payment_type === "SPP" ? String(gradeFee ?? cls?.monthly_fee ?? s.monthly_fee ?? "") : f.amount }));
  };

  const pickType = (t) => {
    setForm((f) => {
      const next = { ...f, payment_type: t };
      if (t === "SPP" && f.student_id) {
        const s = students.find((x) => x.id === f.student_id);
        const cls = classes.find((c) => c.id === s?.class_id);
        const gradeFee = s?.grade ? cls?.grade_fees?.[s.grade] : null;
        next.amount = String(gradeFee ?? cls?.monthly_fee ?? s?.monthly_fee ?? "");
      }
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.post("/payments", { ...form, amount: Number(form.amount) || 0,
        month: Number(form.month), year: Number(form.year) });
      toast.success(`Pembayaran ${form.payment_type} tercatat`);
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
      window.open(`/kwitansi/${res.data.id}`, "_blank");
    } catch (err) {
      toast.error(apiError(err, "Gagal mencatat pembayaran"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Hapus pembayaran ${p.receipt_no} atas nama ${p.student_name}?`)) return;
    try {
      await api.delete(`/payments/${p.id}`);
      toast.success("Pembayaran dihapus");
      load();
    } catch (err) {
      toast.error(apiError(err, "Gagal menghapus pembayaran"));
    }
  };

  const total = payments.reduce((sum, p) => sum + p.amount, 0);

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Pembayaran</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {payments.length} transaksi • Total <span className="font-bold text-primary">{rupiah(total)}</span>
          </p>
        </div>
        <Button onClick={() => setOpen(true)} className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-payment-button">
          <Plus className="h-4 w-4" /> Catat Pembayaran
        </Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-52" data-testid="payment-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Jenis</SelectItem>
            {PAYMENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="w-44" data-testid="payment-month-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Bulan</SelectItem>
            {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="w-36" data-testid="payment-year-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Tahun</SelectItem>
            {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="border-border shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>No. Kwitansi</TableHead>
                <TableHead>Jenis</TableHead>
                <TableHead>Siswa</TableHead>
                <TableHead>Kelas</TableHead>
                <TableHead>Keterangan</TableHead>
                <TableHead>Tanggal Bayar</TableHead>
                <TableHead>Metode</TableHead>
                <TableHead className="text-right">Jumlah</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="py-10 text-center text-muted-foreground" data-testid="payments-empty">
                    Belum ada pembayaran pada periode ini.
                  </TableCell>
                </TableRow>
              ) : payments.map((p) => (
                <TableRow key={p.id} className="transition-colors duration-200 hover:bg-slate-50" data-testid={`payment-row-${p.id}`}>
                  <TableCell className="font-mono text-xs">{p.receipt_no}</TableCell>
                  <TableCell>
                    <Badge className={TYPE_STYLES[p.payment_type] || TYPE_STYLES["Lainnya"]} data-testid={`payment-type-${p.id}`}>{p.payment_type}</Badge>
                  </TableCell>
                  <TableCell className="font-semibold">{p.student_name}</TableCell>
                  <TableCell>{p.class_name}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {p.payment_type === "SPP" ? `${p.month_name} ${p.year}` : (p.description || "-")}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDate(p.paid_at)}</TableCell>
                  <TableCell>{p.method}</TableCell>
                  <TableCell className="text-right font-bold text-primary">{rupiah(p.amount)}</TableCell>
                  <TableCell className="text-right">
                    {p.phone && (
                      <a
                        href={waLink(p.phone, waMessages.confirmation({ parentName: p.parent_name, studentName: p.student_name, monthName: p.month_name, year: p.year, amount: p.amount, receiptNo: p.receipt_no, method: p.method }))}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`wa-confirm-${p.id}`}
                      >
                        <Button variant="ghost" size="icon" title="Kirim konfirmasi via WhatsApp">
                          <MessageCircle className="h-4 w-4 text-emerald-600" />
                        </Button>
                      </a>
                    )}
                    <Link to={`/kwitansi/${p.id}`} target="_blank">
                      <Button variant="ghost" size="icon" data-testid={`print-receipt-${p.id}`}>
                        <Printer className="h-4 w-4 text-primary" />
                      </Button>
                    </Link>
                    <Button variant="ghost" size="icon" onClick={() => remove(p)} data-testid={`delete-payment-${p.id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="payment-form-dialog">
          <DialogHeader>
            <DialogTitle>Catat Pembayaran</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Jenis Pembayaran *</Label>
              <Select value={form.payment_type} onValueChange={pickType}>
                <SelectTrigger data-testid="payment-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Siswa *</Label>
              <Select value={form.student_id} onValueChange={pickStudent}>
                <SelectTrigger data-testid="payment-student-select"><SelectValue placeholder="Pilih siswa" /></SelectTrigger>
                <SelectContent>
                  {students.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name} — {s.class_name} ({s.grade})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.payment_type === "SPP" ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Untuk Bulan</Label>
                  <Select value={String(form.month)} onValueChange={(v) => setForm({ ...form, month: Number(v) })}>
                    <SelectTrigger data-testid="payment-month-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tahun</Label>
                  <Select value={String(form.year)} onValueChange={(v) => setForm({ ...form, year: Number(v) })}>
                    <SelectTrigger data-testid="payment-year-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Keterangan *</Label>
                <Input
                  placeholder={form.payment_type === "Pendaftaran Murid Baru" ? "cth: Biaya pendaftaran murid baru" : `cth: ${form.payment_type === "Buku" ? "Buku Paket Piano Level 1" : form.payment_type === "Ujian" ? "Ujian kenaikan Grade 3" : form.payment_type === "Lomba" ? "Festival Musik Pelajar 2026" : "Keterangan pembayaran"}`}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="payment-description-input"
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Jumlah (Rp) *</Label>
                <Input type="number" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payment-amount-input" />
              </div>
              <div className="space-y-2">
                <Label>Metode</Label>
                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v, account: v === "Transfer" ? "Bank" : "Kas" })}>
                  <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Tunai">Tunai</SelectItem>
                    <SelectItem value="Transfer">Transfer Bank</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Masuk ke Akun</Label>
              <Select value={form.account} onValueChange={(v) => setForm({ ...form, account: v })}>
                <SelectTrigger data-testid="payment-account-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACCOUNT_OPTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Catatan</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="payment-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="payment-cancel-button">Batal</Button>
            <Button onClick={save} disabled={saving || !form.student_id || !form.amount || (form.payment_type !== "SPP" && !form.description)} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="payment-save-button">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Simpan & Cetak
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
